import styled from "@emotion/styled";
import type { FC } from "react";
import translate from "src/lib/translate";
import type { RefillRequirementsProgress } from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";
import { RefillColors } from "src/constants/refillColors";
import HubSectionLabel from "./HubSectionLabel";

// Completion gradient sampled from Figma (node 5805:93924): orange → green, anchored to
// the full bar width and revealed up to the fill (so 1/3 shows orange→yellow-green).
const COMPLETION_GRADIENT = `linear-gradient(90deg, ${RefillColors.sunriseOrange} 0%, ${RefillColors.mint} 100%)`;
// Unfilled track: Primary/Cream at 20% opacity so the hero shows through (Figma "Width Line").
const TRACK_BACKGROUND = `${RefillColors.cream}33`; // #FFFCF3 @ 20%

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: var(--color-white);
  /* Flush on the 24px content column (the 4px header inset lives on HubSectionLabel); 12px
     bottom separates the bar from the requirements card. */
  padding: 0 0 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Track = styled.div`
  position: relative;
  flex: 1;
  height: 4px;
  border-radius: 8px;
  overflow: hidden;
  background: ${TRACK_BACKGROUND};
`;

// Gradient fill anchored to the full track width, revealed up to the fill via clip-path so a
// partial fill shows the matching slice of the gradient (not a compressed one). The clipped-
// away portion exposes the 20% cream track beneath.
const Fill = styled("div", { shouldForwardProp: (p) => p !== "$percent" })<{
  $percent: number;
}>`
  position: absolute;
  inset: 0;
  background: ${COMPLETION_GRADIENT};
  clip-path: inset(
    0 ${({ $percent }) => Math.max(0, Math.round((1 - $percent) * 100))}% 0 0
  );
  transition: clip-path 0.3s ease;
`;

const Count = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  flex-shrink: 0;
`;

/** "Your requirements" label + progress bar + X/Y count. White, sits over the hero. */
const RefillHubProgress: FC<{ progress: RefillRequirementsProgress }> = ({
  progress
}) => (
  <Wrap>
    <HubSectionLabel $onDark>
      {translate("prepareRefillDialog.v2.requirements.label")}
    </HubSectionLabel>
    <Row>
      <Track>
        <Fill $percent={progress.percent} />
      </Track>
      <Count>
        {progress.completed}/{progress.total}
      </Count>
    </Row>
  </Wrap>
);

export default RefillHubProgress;
